import type { ImpersonationTargetWorkspace } from "@unbox/api";
import { Avatar } from "@/components/base/avatar/avatar";
import { Badge } from "@/components/base/badges/badges";
import {
  fullName,
  initials,
  isUnboxStaff,
  type ListUser,
  merchantsByRole,
  roleLabel,
  roleSummaryColor,
  workspaceRoleSummary,
} from "./model";
import { PillList } from "./pill-list";

/** Merchants a role covers before the rest fold into "+N". */
const MAX_MERCHANT_PILLS = 6;

export const UserDetails = ({ user, workspaces }: { user: ListUser; workspaces: ImpersonationTargetWorkspace[] }) => {
  const name = fullName(user);
  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex items-center gap-5">
        <Avatar size="xl" initials={initials(name)} alt={name} />
        <div className="flex min-w-0 flex-col gap-0.5">
          <h3 className="flex flex-wrap items-center gap-2 text-xl font-semibold text-gray-900">
            {name}
            {isUnboxStaff(user) && (
              <Badge size="sm" color="warning">
                Unbox staff
              </Badge>
            )}
          </h3>
          <p className="text-md break-all text-gray-600">{user.email}</p>
        </div>
      </div>

      {workspaces.map((workspace) => {
        const summary = workspaceRoleSummary(workspace);
        return (
          <div key={workspace.id} className="flex flex-col gap-3 border-t border-gray-200 pt-5">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="text-xs font-medium text-gray-500">Workspace</span>
              <Badge size="md" color={roleSummaryColor(summary)}>
                {workspace.name} · {summary}
              </Badge>
              <span className="ml-auto text-sm text-gray-500">
                {workspace.merchants.length} merchant{workspace.merchants.length === 1 ? "" : "s"}
              </span>
            </div>
            {merchantsByRole(workspace).map((group) => (
              <div key={group.role} className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-gray-500">{roleLabel(group.role)} on</span>
                <PillList
                  max={MAX_MERCHANT_PILLS}
                  noun={{ one: "merchant", many: "merchants" }}
                  pills={group.names.map((merchantName) => ({ key: merchantName, label: merchantName, color: "gray" }))}
                />
              </div>
            ))}
            {workspace.merchants.length === 0 && (
              <p className="text-sm text-gray-500">
                {workspace.role === "workspace_admin"
                  ? "Workspace admin. The workspace has no merchants yet."
                  : "No merchant access."}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};
